import { AuthService } from '@core/service/auth.service';
import { ChangeDetectorRef, Component } from '@angular/core';
import { User } from '@core/model/user.model';
import { ExchangeRate, ExchangeRateVersion } from '@app/features/scope-overview/model/exchange-rate.model';
import { CompanyManagementService } from '@app/features/company-management/service/company-management.service';
import { ExchangeRateFilter } from '@core/model/exchange-rate-filter.model';
import { Preference } from '@core/model/user-preferences.interface';
import { TableColumnKey } from '@shared/components/ui-components/scope-ui-table/table-column-key.enum';
import { DataMappingService } from '@app/features/scoping/service/data-mapping.service';
import { Pagination } from '@core/model/definitions/pagination.interface';
import { MappedEntity } from '@app/features/scoping/models/mapped-entity.model';
import { MenuOptions } from '@core/model/definitions/menu-options.interface';
import { PageEvent } from '@angular/material/paginator';
import { SNACKBAR_LENGTH_LONG, SnackbarEventType, SnackbarService } from '@shared/utils/snackbar.service';
import { ScopeUiModalComponent } from '@shared/components/ui-components/scope-ui-modal/scope-ui-modal.component';
import { ModalConfig } from '@core/model/modal-config.model';
import { MatDialog } from '@angular/material/dialog';
import {
  AddEditExchangeRateModalComponent,
} from '@app/features/company-management/components/data/exchange-rates/add-edit-exchange-rate-modal/add-edit-exchange-rate-modal.component';
import { ScopeUiTableSelectMode } from '@shared/components/ui-components/scope-ui-table/scope-ui-table.component';
import { plainToInstance } from 'class-transformer';
import { ExchangeRateService } from '@shared/services/exchange-rate.service';

@Component({
  selector: 'exchange-rates',
  templateUrl: './exchange-rates.component.html',
  styleUrls: ['./exchange-rates.component.scss']
})
export class ExchangeRatesComponent {
  loggedInUser!: User
  exchangeRates: MappedEntity<ExchangeRate>[]
  filter: ExchangeRateFilter = {}
  page = 0
  pageSize = 20
  loading = true
  tableData: Pagination
  menuOptions!: MenuOptions[]
  columns: Preference[] = [
    {
      key: TableColumnKey.BASE_CURRENCY,
      name: 'Base currency',
      field: 'fromCurrencyCode',
      selected: true
    },
    {
      key: TableColumnKey.TO_CURRENCY,
      name: 'Converted to',
      field: 'toCurrencyCode',
      selected: true
    },
    {
      key: TableColumnKey.EXCHANGE_RATE,
      name: 'Exchange rate',
      field: 'exchangeRate',
      selected: true
    },
    {
      key: TableColumnKey.LAST_EDITED,
      name: 'Last updated',
      value: (element) => element.auditMetadata.updatedOn || element.auditMetadata.createdOn,
      selected: true
    },
    {
      key: TableColumnKey.UPDATED_BY,
      name: 'Updated by',
      value: (element) => element.auditMetadata.updatedBy?.fullName || element.auditMetadata.createdBy.fullName,
      selected: true
    },
  ]

  versionHistoryColumns: Preference[] = [
    {
      key: TableColumnKey.DATE_CREATED,
      name: 'Version Date',
      field: 'createdOn',
      selected: true
    },
    {
      key: TableColumnKey.CREATED_BY,
      name: 'Created by',
      field: 'createdBy',
      selected: true
    },
    {
      key: TableColumnKey.EXCHANGE_RATE,
      name: 'Exchange rate',
      field: 'exchangeRate',
      selected: true
    }
  ]

  constructor(private authService: AuthService,
              private manageService: CompanyManagementService,
              private exchangeRateService: ExchangeRateService,
              private mappingService: DataMappingService,
              private snackbarService: SnackbarService,
              private dialog: MatDialog,
              private cdr: ChangeDetectorRef) {
    this.loggedInUser = this.authService.loggedInUser
  }

  ngOnInit() {
    this.getExchangeRates()

    this.menuOptions = [
      {
        callback: this.editExchangeRate,
        name: () => 'Edit',
        icon: () => 'edit'
      },
      {
        callback: this.deleteExchangeRate,
        name: () => 'Delete',
        icon: () => 'delete'
      }
    ]
  }

  getExchangeRates() {
    this.loading = true
    this.cdr.detectChanges()
    this.exchangeRateService.getExchangeRates(this.filter, { page: this.page, pageSize: this.pageSize }).pipe()
      .subscribe(exchangeRates => {
        this.tableData = {
          totalCount: exchangeRates.totalElements,
          page: exchangeRates.number
        }
        exchangeRates.content.forEach(e => {
          e.versionHistory.unshift(plainToInstance(ExchangeRateVersion, { exchangeRate: e.exchangeRate,
            createdBy: e.auditMetadata.updatedBy?.fullName || e.auditMetadata.createdBy.fullName,
            createdOn: e.auditMetadata.updatedOn || e.auditMetadata.createdOn }))
        })
        this.exchangeRates = this.mappingService.transformArray<ExchangeRate>(exchangeRates.content, this.columns, 'versionHistory', false)
        this.loading = false
        this.cdr.detectChanges()
      }, _ => {
        this.loading = false
        this.snackbarService.showDefaultErrorSnackbar()
        this.cdr.detectChanges()
      })
  }

  addExchangeRate() {
    let dialog = this.dialog.open(AddEditExchangeRateModalComponent)
    dialog.componentInstance.currencies = this.manageService.currencies
    dialog.componentInstance.onSubmit.subscribe((exchangeRate) => {
      this.exchangeRateService.addExchangeRate(exchangeRate).subscribe({
        next: () => {
          dialog.close()
          this.page = 0
          this.getExchangeRates()
        },
        error: (error) => {
          dialog.componentInstance.loading = false
          if (error.status == 409) {
            this.snackbarService.showSnackbar("This exchange rate already exists. Select the existing exchange rate to review and update.", SNACKBAR_LENGTH_LONG, SnackbarEventType.WARNING)
          } else {
            this.snackbarService.showDefaultErrorSnackbar()
          }
        },
      })
    })
  }

  editExchangeRate = ({ entity }: MappedEntity<ExchangeRate>) => {
    let dialog = this.dialog.open(AddEditExchangeRateModalComponent)
    dialog.componentInstance.currencies = this.manageService.currencies
    dialog.componentInstance.exchangeRate = entity
    dialog.componentInstance.onSubmit.subscribe((exchangeRate) => {
      this.exchangeRateService.editExchangeRate(exchangeRate).subscribe({
        next: () => {
          dialog.close()
          this.getExchangeRates()
        },
        error: () => {
          dialog.componentInstance.loading = false
          this.snackbarService.showDefaultErrorSnackbar()
        }
      })
    })
  }

  private deleteExchangeRate = ({ entity }: MappedEntity<ExchangeRate>) => {
    let dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Delete Exchange Rate`,
        `Are you sure you want to delete the exchange rate for "${entity.fromCurrencyCode} to ${entity.toCurrencyCode}"?`,
        'Delete',
        undefined,
        () => {
          dialog.close();
          this.exchangeRateService.deleteExchangeRate(entity.id).subscribe({
            next: () => {
              this.getExchangeRates()
            },
            error: (error) => {
              this.snackbarService.showDefaultErrorSnackbar()
              return error
            },
          })
        },
        undefined,
        [],
        false,
        true
      ),
    })
  }

  onChangePage(pageEvent: PageEvent) {
    this.page = pageEvent.pageIndex
    this.getExchangeRates()
  }

  protected readonly ScopeUiTableSelectMode = ScopeUiTableSelectMode;
}
